import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Car, Upload, CheckCircle, XCircle, Loader2, FileText, User } from 'lucide-react';

const FileUploadField = ({ label, fileType, token, value, onUploaded, accept, testId }) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/driver-invites/${token}/upload?file_type=${fileType}`, formData);
      onUploaded(res.data.file_url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to upload ${label}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-gray-300">{label} *</Label>
      <label
        data-testid={testId}
        className={`mt-1 flex items-center gap-3 border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${value ? 'border-green-500 bg-green-500/10' : 'border-gray-600 hover:border-gold-500'}`}
      >
        {uploading ? (
          <Loader2 className="w-5 h-5 text-gold-500 animate-spin" />
        ) : value ? (
          <CheckCircle className="w-5 h-5 text-green-500" />
        ) : (
          <Upload className="w-5 h-5 text-gray-400" />
        )}
        <span className={`text-sm ${value ? 'text-green-400' : 'text-gray-400'}`}>
          {uploading ? 'Uploading...' : value ? 'Uploaded — click to replace' : 'Click to upload'}
        </span>
        <input type="file" className="hidden" accept={accept} onChange={handleFile} />
      </label>
    </div>
  );
};

export default function DriverApplication() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [inviteStatus, setInviteStatus] = useState('loading'); // loading, valid, invalid
  const [inviteError, setInviteError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirm_password: '',
    vehicle_make: '', vehicle_model: '', vehicle_year: '', license_plate: '',
    profile_photo_url: '', chauffeur_license_url: '', insurance_doc_url: ''
  });

  useEffect(() => {
    axios.get(`${API}/driver-invites/${token}`)
      .then((res) => {
        setForm((f) => ({ ...f, email: res.data.email || '', name: res.data.name || '' }));
        setInviteStatus('valid');
      })
      .catch((err) => {
        setInviteError(err.response?.data?.detail || 'Invalid invite link');
        setInviteStatus('invalid');
      });
  }, [token]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (form.password !== form.confirm_password) return toast.error('Passwords do not match');
    if (!form.profile_photo_url) return toast.error('Please upload a profile photo');
    if (!form.chauffeur_license_url) return toast.error("Please upload your chauffeur's license");
    if (!form.insurance_doc_url) return toast.error('Please upload your insurance document');
    setSubmitting(true);
    try {
      const { confirm_password, ...payload } = form;
      const res = await axios.post(`${API}/driver-invites/${token}/apply`, payload);
      setSubmitted(true);
      toast.success('Application submitted!');
      setTimeout(() => {
        login(res.data.access_token, res.data.user);
        navigate('/dashboard');
      }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (inviteStatus === 'loading') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
      </div>
    );
  }

  if (inviteStatus === 'invalid') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="bg-gray-900 border-gray-800 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Invite Not Valid</h2>
            <p className="text-gray-400" data-testid="invite-error-message">{inviteError}</p>
            <p className="text-gray-500 text-sm mt-4">Please contact Onyx Reserve for a new invite link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="bg-gray-900 border-gray-800 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2" data-testid="application-success-message">Application Submitted!</h2>
            <p className="text-gray-400">Our team will review your application and notify you by email. Redirecting you to your account...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Car className="w-8 h-8 text-gold-500" />
            <h1 className="text-3xl font-bold text-white">Black <span className="text-gold-500">HDrive</span></h1>
          </div>
          <p className="text-gray-400">Driver Application</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><User className="w-5 h-5 text-gold-500" /> Personal Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Full Name *</Label>
                  <Input data-testid="application-name-input" required value={form.name} onChange={set('name')} className="bg-gray-800 border-gray-700 text-white" placeholder="John Smith" />
                </div>
                <div>
                  <Label className="text-gray-300">Phone *</Label>
                  <Input data-testid="application-phone-input" required value={form.phone} onChange={set('phone')} className="bg-gray-800 border-gray-700 text-white" placeholder="(305) 555-0123" />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Email *</Label>
                <Input data-testid="application-email-input" required type="email" value={form.email} onChange={set('email')} className="bg-gray-800 border-gray-700 text-white" placeholder="you@email.com" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Password *</Label>
                  <Input data-testid="application-password-input" required type="password" value={form.password} onChange={set('password')} className="bg-gray-800 border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Confirm Password *</Label>
                  <Input data-testid="application-confirm-password-input" required type="password" value={form.confirm_password} onChange={set('confirm_password')} className="bg-gray-800 border-gray-700 text-white" />
                </div>
              </div>
              <FileUploadField label="Profile Photo" fileType="profile" token={token} accept="image/*" value={form.profile_photo_url} onUploaded={(url) => setForm((f) => ({ ...f, profile_photo_url: url }))} testId="upload-profile-photo" />
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><FileText className="w-5 h-5 text-gold-500" /> Documents</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <FileUploadField label="Chauffeur's License (photo or PDF)" fileType="license" token={token} accept="image/*,application/pdf" value={form.chauffeur_license_url} onUploaded={(url) => setForm((f) => ({ ...f, chauffeur_license_url: url }))} testId="upload-license" />
              <FileUploadField label="Insurance Document (photo or PDF)" fileType="insurance" token={token} accept="image/*,application/pdf" value={form.insurance_doc_url} onUploaded={(url) => setForm((f) => ({ ...f, insurance_doc_url: url }))} testId="upload-insurance" />
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Car className="w-5 h-5 text-gold-500" /> Vehicle Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Make *</Label>
                  <Input data-testid="application-vehicle-make-input" required value={form.vehicle_make} onChange={set('vehicle_make')} className="bg-gray-800 border-gray-700 text-white" placeholder="Cadillac" />
                </div>
                <div>
                  <Label className="text-gray-300">Model *</Label>
                  <Input data-testid="application-vehicle-model-input" required value={form.vehicle_model} onChange={set('vehicle_model')} className="bg-gray-800 border-gray-700 text-white" placeholder="Escalade" />
                </div>
                <div>
                  <Label className="text-gray-300">Year *</Label>
                  <Input data-testid="application-vehicle-year-input" required value={form.vehicle_year} onChange={set('vehicle_year')} className="bg-gray-800 border-gray-700 text-white" placeholder="2026" />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">License Plate *</Label>
                <Input data-testid="application-license-plate-input" required value={form.license_plate} onChange={set('license_plate')} className="bg-gray-800 border-gray-700 text-white" placeholder="ABC 1234" />
              </div>
            </CardContent>
          </Card>

          <Button data-testid="application-submit-button" type="submit" disabled={submitting} className="w-full bg-gold-500 hover:bg-gold-600 text-black font-bold py-6 text-lg">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
            Submit Application
          </Button>
        </form>
      </div>
    </div>
  );
}
